import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Switch,
  FormGroup,
  FormControlLabel,
  Divider,
  Button,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  ShoppingCart as OrderIcon,
  Inventory as InventoryIcon,
  LocalShipping as ShippingIcon,
  Warning as AlertIcon,
  MoreVert as MoreIcon,
  Delete as DeleteIcon,
  Done as DoneIcon,
} from '@mui/icons-material';
import { DashboardLayout } from '../../components/layout/DashboardLayout';

interface Notification {
  id: string;
  type: 'order' | 'inventory' | 'shipping' | 'alert';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
}

export const NotificationsCenter: React.FC = () => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedNotification, setSelectedNotification] = useState<string | null>(null);
  const [notificationPreferences, setNotificationPreferences] = useState({
    orders: true,
    inventory: true,
    shipping: true,
    alerts: true,
    email: true,
    push: true,
    sms: false,
  });

  // Mock notifications data
  const [notifications, setNotifications] = useState<Notification[]>([
    {
      id: '1',
      type: 'order',
      title: 'New Order Received',
      message: 'Order #12345 for Brake Pads has been placed by AutoFix Garage',
      timestamp: new Date(2024, 2, 15, 10, 30),
      read: false,
    },
    {
      id: '2',
      type: 'inventory',
      title: 'Low Stock Alert',
      message: 'Oil Filters (SKU: FLT-001) stock level below minimum threshold',
      timestamp: new Date(2024, 2, 15, 9, 45),
      read: false,
    },
    {
      id: '3',
      type: 'shipping',
      title: 'Order Shipped',
      message: 'Order #12340 has been shipped via FastFreight Couriers',
      timestamp: new Date(2024, 2, 14, 16, 20),
      read: true,
    },
    {
      id: '4',
      type: 'alert',
      title: 'Price Update',
      message: 'Supplier has updated prices for 15 items in your inventory',
      timestamp: new Date(2024, 2, 14, 14, 15),
      read: true,
    },
  ]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, notificationId: string) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedNotification(notificationId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedNotification(null);
  };

  const handleMarkAsRead = () => {
    if (selectedNotification) {
      setNotifications(notifications.map(notification =>
        notification.id === selectedNotification
          ? { ...notification, read: true }
          : notification
      ));
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    if (selectedNotification) {
      setNotifications(notifications.filter(
        notification => notification.id !== selectedNotification
      ));
    }
    handleMenuClose();
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'order':
        return <OrderIcon color="primary" />;
      case 'inventory':
        return <InventoryIcon color="warning" />;
      case 'shipping':
        return <ShippingIcon color="info" />;
      case 'alert':
        return <AlertIcon color="error" />;
      default:
        return <NotificationsIcon />;
    }
  };

  const formatTimestamp = (date: Date) => {
    return date.toLocaleString('en-ZA', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handlePreferenceChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setNotificationPreferences({
      ...notificationPreferences,
      [event.target.name]: event.target.checked,
    });
  };

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Notifications Center
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6">Recent Notifications</Typography>
                  <Button
                    size="small"
                    onClick={() => setNotifications(notifications.map(n => ({ ...n, read: true })))}
                  >
                    Mark All as Read
                  </Button>
                </Box>
                <List>
                  {notifications.map((notification) => (
                    <React.Fragment key={notification.id}>
                      <ListItem
                        sx={{
                          bgcolor: notification.read ? 'transparent' : 'action.hover',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <ListItemIcon>
                          {getNotificationIcon(notification.type)}
                        </ListItemIcon>
                        <ListItemText
                          primary={notification.title}
                          secondary={
                            <React.Fragment>
                              <Typography
                                component="span"
                                variant="body2"
                                color="text.primary"
                              >
                                {notification.message}
                              </Typography>
                              <br />
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                              >
                                {formatTimestamp(notification.timestamp)}
                              </Typography>
                            </React.Fragment>
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={(e) => handleMenuOpen(e, notification.id)}
                          >
                            <MoreIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                      <Divider variant="inset" component="li" />
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Notification Settings
                </Typography>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Notification Types
                </Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notificationPreferences.orders}
                        onChange={handlePreferenceChange}
                        name="orders"
                      />
                    }
                    label="Order Updates"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notificationPreferences.inventory}
                        onChange={handlePreferenceChange}
                        name="inventory"
                      />
                    }
                    label="Inventory Alerts"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notificationPreferences.shipping}
                        onChange={handlePreferenceChange}
                        name="shipping"
                      />
                    }
                    label="Shipping Updates"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notificationPreferences.alerts}
                        onChange={handlePreferenceChange}
                        name="alerts"
                      />
                    }
                    label="System Alerts"
                  />
                </FormGroup>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Delivery Methods
                  </Typography>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={notificationPreferences.email}
                          onChange={handlePreferenceChange}
                          name="email"
                        />
                      }
                      label="Email Notifications"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={notificationPreferences.push}
                          onChange={handlePreferenceChange}
                          name="push"
                        />
                      }
                      label="Push Notifications"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={notificationPreferences.sms}
                          onChange={handlePreferenceChange}
                          name="sms"
                        />
                      }
                      label="SMS Notifications"
                    />
                  </FormGroup>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleMarkAsRead}>
            <ListItemIcon>
              <DoneIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Mark as read</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleDelete}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        </Menu>
      </Box>
    </DashboardLayout>
  );
}; 